import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Select from '../../../components/Select';
import ModulesRoles from './ModulesRoles/ModulesRoles';
import InputFile from '../../../components/InputFile';
import {
  FaProjectDiagram, FaDollarSign, FaCalendarAlt, FaComment,
  FaFolder, FaImage, FaClock, FaTag, FaLink, FaTable,
  FaWhatsapp, FaBrain, FaShieldAlt, FaChevronLeft, FaChevronRight,
  FaSave, FaTimes,
} from 'react-icons/fa';
import { proyectService } from '../../../services/proyectService';
import { proyectTypesService } from '../../../services/proyectTypesService';
import { ProyectManagerService } from '../../../services/ProyectManagerService'
import { attachmentService } from '../../../services/attachmentService';
import { toastError, toastSuccess } from '../../../utils/toast';
import { validateRequired, validateMinLength, validateMaxLength } from '../../../utils/validations';
import { getModuleConfigById } from '../../../utils/moduleConfigs';

const STEPS = [
  { label: 'General',      icon: FaProjectDiagram },
  { label: 'Plataforma',   icon: FaWhatsapp        },
  { label: 'IA & Drive',   icon: FaBrain           },
  { label: 'Suscripción',  icon: FaDollarSign      },
  { label: 'Permisos',     icon: FaShieldAlt       },
];

const AddProyect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isEdit, proyectEdit } = location.state || { isEdit: false, proyectEdit: null };

  const [step, setStep] = useState(0);
  const [proyectTypes, setProyectTypes] = useState([]);
  const [proyectManagers, setProyectManagers] = useState([]);
  const [isWhatsapp, setIsWhatsapp] = useState(false);

  const [platforms] = useState([
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' },
  ]);
  const [statusOptions] = useState([
    { value: 'active', label: 'Activo' },
    { value: 'inactive', label: 'Inactivo' },
  ]);

  const [proyect, setProyect] = useState({
    name: '', description: '', project_type: '', project_manager_id: '',
    platform: '', status: 'active', token: '', profile_photo: '',
    phone_number_id: '', kb_table: '', drive_link: '', query_name: '',
    google_drive_folder_id: '', last_train: '', message_teplate: '',
    image_template: '', document_template: '', suscription: '', price: '', next_payment: '',
    modules: getModuleConfigById(0),
  });

  const [errors, setErrors] = useState({ name: '', description: '', project_type: '' });

  useEffect(() => {
    if (isEdit && proyectEdit) {
      const updatedModules = proyectEdit.modules?.map(module => {
        if (module.module === 'chats' && !module.special_actions.hasOwnProperty('can_block_contact')) {
          return { ...module, special_actions: { ...module.special_actions, can_block_contact: false } };
        }
        return module;
      }) || [];
      setProyect({
        name: proyectEdit.name || '',
        description: proyectEdit.description || '',
        project_type: proyectEdit.project_type || '',
        project_manager_id: proyectEdit.project_manager_id || '',
        platform: proyectEdit.platform || '',
        status: proyectEdit.status || 'active',
        token: proyectEdit.token || '',
        profile_photo: proyectEdit.profile_photo || '',
        phone_number_id: proyectEdit.phone_number_id || '',
        kb_table: proyectEdit.kb_table || '',
        drive_link: proyectEdit.drive_link || '',
        query_name: proyectEdit.query_name || '',
        google_drive_folder_id: proyectEdit.google_drive_folder_id || '',
        last_train: proyectEdit.last_train ? proyectEdit.last_train.slice(0, 16) : '',
        message_teplate: proyectEdit.message_teplate || '',
        image_template: proyectEdit.image_template || '',
        document_template: proyectEdit.document_template || '',
        suscription: proyectEdit.suscription || '',
        price: proyectEdit.price || '',
        next_payment: proyectEdit.next_payment ? proyectEdit.next_payment.slice(0, 10) : '',
        modules: updatedModules,
      });
      setIsWhatsapp(proyectEdit.platform === 'whatsapp');
    }
  }, [isEdit, proyectEdit]);

  useEffect(() => {
    const fetchProyectTypes = async () => {
      try {
        const tipos = await proyectTypesService.getAll();
        setProyectTypes(tipos.map(t => ({ value: t.id, label: t.name })));
      } catch (error) {
        console.error('Error al obtener tipos de proyectos:', error);
      }
    };
    fetchProyectTypes();
    fetchProyectManagers();
  }, []);

  const fetchProyectManagers = async () => {
    try {
      const managers = await ProyectManagerService.getAll();
      setProyectManagers(managers.map(m => ({ value: m.id, label: m.name })));
    } catch (error) {
      console.error('Error al obtener gerentes:', error);
    }
  };

  const validateStep0 = () => {
    const nameErr = validateRequired(proyect.name)
      || validateMinLength(proyect.name, 3, 'Mínimo 3 caracteres')
      || validateMaxLength(proyect.name, 100, 'Máximo 100 caracteres');
    const descErr = validateRequired(proyect.description)
      || validateMinLength(proyect.description, 3, 'Mínimo 3 caracteres')
      || validateMaxLength(proyect.description, 150, 'Máximo 150 caracteres');
    const typeErr = validateRequired(proyect.project_type);
    setErrors({ name: nameErr || '', description: descErr || '', project_type: typeErr || '' });
    return !nameErr && !descErr && !typeErr;
  };

  const handleNext = () => {
    if (step === 0 && !validateStep0()) {
      toastError('Completa los campos requeridos antes de continuar');
      return;
    }
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const handlePrev = () => setStep(s => Math.max(s - 1, 0));

  const resolveProfilePhoto = async () => {
    if (proyect.profile_photo && proyect.profile_photo instanceof File) {
      const response = await attachmentService.uploadImage(proyect.profile_photo);
      if (!response.success) { toastError(response.message); return null; }
      return "https://apidasboard.canteradigital.mx/" + response.filePath;
    }
    return proyect.profile_photo;
  };

  const handleSubmit = async () => {
    if (!validateStep0()) { toastError('Revisa los campos requeridos'); return; }
    const photoUrl = await resolveProfilePhoto();
    if (photoUrl === null) return;
    try {
      await proyectService.createProyect({ ...proyect, profile_photo: photoUrl });
      toastSuccess('Proyecto creado correctamente');
      navigate('/proyects');
    } catch {
      toastError('Error al crear el proyecto');
    }
  };

  const handleEdit = async () => {
    if (!validateStep0()) { toastError('Revisa los campos requeridos'); return; }
    const photoUrl = await resolveProfilePhoto();
    if (photoUrl === null) return;
    try {
      await proyectService.updateProyect(proyectEdit.id, { ...proyect, profile_photo: photoUrl });
      toastSuccess('Proyecto editado correctamente');
      navigate('/proyects');
    } catch {
      toastError('Error al editar el proyecto');
    }
  };

  const isLast = step === STEPS.length - 1;

  return (
    <div className="bg-surface-card border border-white/[0.06] rounded-xl overflow-hidden">

      {/* ── Wizard header sticky ── */}
      <div
        className="sticky top-0 z-20"
        style={{ backgroundColor: '#0d0e1e', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        {/* Steps bar */}
        <div className="flex items-center px-6 pt-5 pb-3 gap-0">
          {STEPS.map((s, i) => {
            const done    = i < step;
            const current = i === step;
            const Icon    = s.icon;
            return (
              <React.Fragment key={i}>
                <button
                  type="button"
                  onClick={() => setStep(i)}
                  className="flex flex-col items-center gap-1 flex-shrink-0"
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200"
                    style={
                      current ? {
                        backgroundColor: '#DC1F26',
                        color: '#fff',
                        boxShadow: '0 0 12px rgba(220,31,38,0.4)',
                      } : done ? {
                        backgroundColor: 'rgba(34,197,94,0.15)',
                        color: '#4ADE80',
                        border: '1px solid rgba(34,197,94,0.3)',
                      } : {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        color: '#3A3D52',
                        border: '1px solid rgba(255,255,255,0.07)',
                      }
                    }
                  >
                    {done ? '✓' : <Icon size={12} />}
                  </div>
                  <span
                    className="text-[10px] font-medium whitespace-nowrap"
                    style={{ color: current ? '#E8EAF0' : done ? '#4ADE80' : '#3A3D52' }}
                  >
                    {s.label}
                  </span>
                </button>

                {/* Connector */}
                {i < STEPS.length - 1 && (
                  <div
                    className="flex-1 h-px mx-2 mt-[-10px]"
                    style={{ backgroundColor: done ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.06)' }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between px-6 py-3">
          <button
            type="button"
            onClick={() => navigate('/proyects')}
            className="flex items-center gap-1.5 text-xs font-medium transition-colors"
            style={{ color: '#4A5270' }}
            onMouseEnter={e => e.currentTarget.style.color = '#E8EAF0'}
            onMouseLeave={e => e.currentTarget.style.color = '#4A5270'}
          >
            <FaTimes size={11} /> Cancelar
          </button>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={handlePrev}
                leftIcon={<FaChevronLeft size={10} />}>
                Anterior
              </Button>
            )}
            {!isLast && (
              <Button variant="outline" size="sm" onClick={handleNext}
                rightIcon={<FaChevronRight size={10} />}>
                Siguiente
              </Button>
            )}
            <Button variant="primary" size="sm"
              onClick={isEdit ? handleEdit : handleSubmit}
              leftIcon={<FaSave size={11} />}>
              {isEdit ? 'Guardar cambios' : 'Crear proyecto'}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Contenido del step ── */}
      <div className="p-6">

        {/* Step 0 — General */}
        {step === 0 && (
          <div className="space-y-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#4A5270' }}>
                Información básica del proyecto
              </p>
              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Nombre del Proyecto" name="name" type="text" required size="lg"
                  value={proyect.name} leftIcon={<FaProjectDiagram />}
                  onChange={(e) => { setProyect({ ...proyect, name: e.target.value }); }}
                  error={errors.name}
                />
                <Input
                  label="Descripción" name="description" type="text" required size="lg"
                  value={proyect.description} leftIcon={<FaProjectDiagram />}
                  onChange={(e) => setProyect({ ...proyect, description: e.target.value })}
                  error={errors.description}
                />
                <InputFile
                  label="Imagen de portada" name="image"
                  placeholder="imagen de portada"
                  value={proyect.profile_photo}
                  onChange={(file) => setProyect(prev => ({ ...prev, profile_photo: file }))}
                  error={errors.profile_photo}
                />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#4A5270' }}>
                Configuración
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Select
                  id="project_type_id" name="project_type_id" label="Tipo de Proyecto" required
                  options={proyectTypes} value={proyect.project_type}
                  onChange={(e) => {
                    const newTypeId = e.target.value;
                    setProyect({ ...proyect, project_type: newTypeId, modules: getModuleConfigById(newTypeId) });
                  }}
                  placeholder="Selecciona un tipo" isSearchable error={errors.project_type}
                />
                <Select
                  id="project_manager_id" name="project_manager_id" label="Gerente de Proyecto"
                  options={proyectManagers} value={proyect.project_manager_id}
                  onChange={(e) => setProyect({ ...proyect, project_manager_id: e.target.value })}
                  placeholder="Selecciona un gerente" isSearchable={false}
                />
                <Select
                  id="platform" name="platform" label="Plataforma de Mensajería"
                  options={platforms} value={proyect.platform}
                  onChange={(e) => {
                    setProyect({ ...proyect, platform: e.target.value });
                    setIsWhatsapp(e.target.value === 'whatsapp');
                  }}
                  placeholder="Selecciona una plataforma" isSearchable={false}
                />
                <Select
                  id="status" name="status" label="Estado del Proyecto"
                  options={statusOptions} value={proyect.status}
                  onChange={(e) => setProyect({ ...proyect, status: e.target.value })}
                  placeholder="Selecciona un estado" isSearchable={false}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 1 — Plataforma */}
        {step === 1 && (
          <div className="space-y-6">
            {isWhatsapp ? (
              <>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#4A5270' }}>
                    Credenciales WhatsApp Business
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Token de Acceso" name="token" type="text" required size="lg"
                      value={proyect.token} leftIcon={<FaWhatsapp />}
                      onChange={(e) => setProyect({ ...proyect, token: e.target.value })}
                    />
                    <Input
                      label="Phone Number ID" name="phone_number_id" type="text" required size="lg"
                      value={proyect.phone_number_id} leftIcon={<FaWhatsapp />}
                      onChange={(e) => setProyect({ ...proyect, phone_number_id: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#4A5270' }}>
                    Plantillas de Mensajes Meta
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Nombre de Plantilla de Texto" name="message_teplate" type="text" size="lg"
                      value={proyect.message_teplate} leftIcon={<FaComment />}
                      onChange={(e) => setProyect({ ...proyect, message_teplate: e.target.value })}
                      placeholder="Ej: bienvenida_agente"
                    />
                    <Input
                      label="Nombre de Plantilla de Imagen" name="image_template" type="text" size="lg"
                      value={proyect.image_template} leftIcon={<FaImage />}
                      onChange={(e) => setProyect({ ...proyect, image_template: e.target.value })}
                      placeholder="Ej: envio_imagenes"
                    />
                    <Input
                      label="Nombre de Plantilla de Documento" name="document_template" type="text" size="lg"
                      value={proyect.document_template} leftIcon={<FaFolder />}
                      onChange={(e) => setProyect({ ...proyect, document_template: e.target.value })}
                      placeholder="Ej: envio_documentos"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div
                className="flex flex-col items-center justify-center py-16 rounded-xl"
                style={{ backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <FaWhatsapp size={32} style={{ color: '#2A2E45', marginBottom: 12 }} />
                <p className="text-sm" style={{ color: '#3A3D52' }}>
                  Esta sección aplica solo cuando la plataforma es <strong>WhatsApp</strong>.
                </p>
                <p className="text-xs mt-1" style={{ color: '#2A2E45' }}>
                  Regresa al paso General para seleccionar la plataforma.
                </p>
              </div>
            )}

            {/* Google Drive — siempre visible */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#4A5270' }}>
                Integración con Google Drive
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Carpeta Google Drive (ID)" name="google_drive_folder_id" type="text" size="lg"
                  value={proyect.google_drive_folder_id} leftIcon={<FaFolder />}
                  onChange={(e) => setProyect({ ...proyect, google_drive_folder_id: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — IA & Drive */}
        {step === 2 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#4A5270' }}>
              Base de conocimientos y configuración del agente IA
            </p>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Tabla de Base de Conocimientos" name="kb_table" type="text" size="lg"
                value={proyect.kb_table} leftIcon={<FaTable />}
                onChange={(e) => setProyect({ ...proyect, kb_table: e.target.value })}
              />
              <Input
                label="Enlace de Drive (URL)" name="drive_link" type="text" size="lg"
                value={proyect.drive_link} leftIcon={<FaLink />}
                onChange={(e) => setProyect({ ...proyect, drive_link: e.target.value })}
              />
              <Input
                label="Nombre de Función de Query" name="query_name" type="text" size="lg"
                value={proyect.query_name} leftIcon={<FaProjectDiagram />}
                onChange={(e) => setProyect({ ...proyect, query_name: e.target.value })}
              />
              <Input
                label="Última Capacitación IA" name="last_train" type="datetime-local" size="lg"
                value={proyect.last_train} leftIcon={<FaClock />}
                onChange={(e) => setProyect({ ...proyect, last_train: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* Step 3 — Suscripción */}
        {step === 3 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#4A5270' }}>
              Plan contratado y facturación
            </p>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Tipo de Suscripción" name="suscription" type="text" size="lg"
                value={proyect.suscription} leftIcon={<FaTag />}
                onChange={(e) => setProyect({ ...proyect, suscription: e.target.value })}
                placeholder="Ej: Básico, Pro, Enterprise"
              />
              <Input
                label="Precio (MXN)" name="price" type="text" size="lg"
                value={proyect.price} leftIcon={<FaDollarSign />}
                onChange={(e) => setProyect({ ...proyect, price: e.target.value })}
                placeholder="Ej: 2500"
              />
              <Input
                label="Próximo Pago" name="next_payment" type="date" size="lg"
                value={proyect.next_payment} leftIcon={<FaCalendarAlt />}
                onChange={(e) => setProyect({ ...proyect, next_payment: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* Step 4 — Permisos */}
        {step === 4 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#4A5270' }}>
              Módulos y permisos del proyecto
            </p>
            <p className="text-xs mb-4" style={{ color: '#3E4460' }}>
              Define qué módulos y acciones están disponibles para los usuarios de este proyecto.
            </p>
            <ModulesRoles
              currentModules={proyect.modules || []}
              onModulesChange={(updatedModules) =>
                setProyect(prev => ({ ...prev, modules: updatedModules }))
              }
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default AddProyect;
